import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import React from 'react';
import Text from '@/components/common/Text';
import Pagination from '@/components/common/Pagination';
import UserTable from '@/components/admin/UserTable';
import UserFilters from '@/components/admin/UserFilters';
import type { UserFilterType } from '@/types/admin.types';
import UserStats from '@/components/admin/UserStats';
import { getUserList } from '@/apis/admin';
import { QUERY_KEY } from '@/constants/queryKey';
import { useQuery } from '@tanstack/react-query';
import * as S from './style';

const AdminUserManagePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Sync filters with URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const currentLimit = parseInt(searchParams.get('limit') || '10', 10) || 10;
  
  const [filters, setFilters] = useState<UserFilterType & { page: number; limit: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }>({
    page: !isNaN(currentPage) && currentPage > 0 ? currentPage : 1,
    limit: !isNaN(currentLimit) && currentLimit > 0 && currentLimit <= 100 ? currentLimit : 10,
    search: searchParams.get('search') || undefined,
    subscription_status: (searchParams.get('subscription_status') as 'free' | 'active' | 'expired') || undefined,
    start_date: searchParams.get('start_date') || undefined,
    end_date: searchParams.get('end_date') || undefined,
    user_type: (searchParams.get('user_type') as 'all' | 'user' | 'admin') || undefined,
    sortBy: searchParams.get('sortBy') || undefined,
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || undefined,
  });

  const handleFilterChange = (newFilters: UserFilterType) => {
    const updatedFilters: UserFilterType & { page: number; limit: number } = { 
      ...filters,
      ...newFilters,
      page: 1,
      limit: filters.limit || 10,
    };
    setFilters(updatedFilters);
    // Update URL params
    const params = new URLSearchParams();
          Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              // Skip user_type = 'all' (default, don't need to include in URL)
              if (key === 'user_type' && value === 'all') {
                return;
              }
              params.append(key, String(value));
            }
          });
    setSearchParams(params);
  };

  const handleReset = () => {
    const resetFilters: UserFilterType & { page: number; limit: number; sortBy?: string; sortOrder?: 'asc' | 'desc' } = {
      page: 1,
      limit: 10,
      search: undefined,
      subscription_status: undefined,
      start_date: undefined,
      end_date: undefined,
      user_type: undefined,
      sortBy: undefined,
      sortOrder: undefined,
    };
    setFilters(resetFilters);
    setSearchParams({});
  };

  // Sync filters when URL params change (e.g., from pagination)
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlLimit = parseInt(searchParams.get('limit') || '10', 10);
    
    // Only update if values are valid numbers and different
    if (!isNaN(urlPage) && !isNaN(urlLimit) && urlPage > 0 && urlLimit > 0) {
      if (filters.page !== urlPage || filters.limit !== urlLimit) {
        setFilters(prev => ({
          ...prev,
          page: urlPage,
          limit: urlLimit,
        }));
      }
    }
  }, [searchParams, filters.page, filters.limit]);

  return (
    <S.Container>
      <Text fontWeight={700} fontSize='1.5625rem' margin='0 0 1.25rem 0'>
        전체 사용자
      </Text>

      <UserStats />

      <UserFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />

      <UserTable 
        filters={filters} 
        onSortChange={(sortBy, sortOrder) => {
          const updatedFilters = {
            ...filters,
            sortBy,
            sortOrder,
            page: 1, // Reset to page 1 when sorting changes
          };
          setFilters(updatedFilters);
          const params = new URLSearchParams();
          Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              // Skip user_type = 'all' (default, don't need to include in URL)
              if (key === 'user_type' && value === 'all') {
                return;
              }
              params.append(key, String(value));
            }
          });
          setSearchParams(params);
        }}
      />

      <UserPaginationWrapper filters={filters} />
    </S.Container>
  );
};

// Separate component for pagination to avoid re-rendering table
const UserPaginationWrapper = ({ filters }: { filters: UserFilterType & { page: number; limit: number } }) => {
  const { data } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.USER_LIST, filters],
    queryFn: () => {
      // Ensure page and limit are valid numbers
      const safeFilters = {
        ...filters,
        page: filters.page && !isNaN(filters.page) && filters.page > 0 ? filters.page : 1,
        limit: filters.limit && !isNaN(filters.limit) && filters.limit > 0 && filters.limit <= 100 ? filters.limit : 10,
      };
      return getUserList(safeFilters);
    },
  });

  if (!data?.data || data.data.total === 0) {
    return null;
  }

  return (
    <Pagination
      page={filters.page}
      totalCount={data.data.total}
      size={filters.limit}
      showInfo={true}
    />
  );
};

export default AdminUserManagePage;
